/**
 * Token-based fuzzy name matching utility
 * Port of the Python name_match.py implementation
 */

/**
 * Calculate similarity between two strings using a simple sequence matcher
 * @param {string} str1
 * @param {string} str2
 * @returns {number} - Similarity score between 0 and 1
 */
function calculateSimilarity(str1, str2) {
  if (str1 === str2) return 1.0;
  if (!str1 || !str2) return 0.0;

  const len1 = str1.length;
  const len2 = str2.length;

  // Simple implementation based on longest common subsequence
  const dp = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const lcs = dp[len1][len2];
  return (2 * lcs) / (len1 + len2);
}

/**
 * Clean a name by applying transformations
 * @param {string} name
 * @returns {string}
 */
function cleanName(name) {
  if (!name) return "";

  // German diacritic mapping
  const germanMap = {
    ö: "oe",
    Ö: "oe",
    ü: "ue",
    Ü: "ue",
    ä: "ae",
    Ä: "ae",
    ß: "ss",
  };

  let cleaned = name;

  // Apply German diacritic replacement
  for (const [char, replacement] of Object.entries(germanMap)) {
    cleaned = cleaned.split(char).join(replacement);
  }

  // Unicode normalization NFD to decompose accents, then remove combining marks
  cleaned = cleaned.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // Convert to lowercase
  cleaned = cleaned.toLowerCase();

  // Remove titles
  const titleRegex = /\b(mr|mrs|ms|dr|prof|sr|sra|srta|don|doña)\b\.?/gi;
  cleaned = cleaned.replace(titleRegex, "");

  // Replace punctuation with spaces
  cleaned = cleaned.replace(/[,.\-'\s]+/g, " ");

  // Strip and collapse whitespace
  cleaned = cleaned.trim().replace(/\s+/g, " ");

  return cleaned;
}

/**
 * Get word count after cleaning but before dropping single chars
 * @param {string} name
 * @returns {number}
 */
function rawWordCount(name) {
  const cleaned = cleanName(name);
  return cleaned ? cleaned.split(" ").length : 0;
}

/**
 * Split name into full words and initials (before dropping single chars)
 * @param {string} name
 * @returns {Object} - { full: string[], initials: string[] }
 */
function splitTokens(name) {
  const cleaned = cleanName(name);
  if (!cleaned) return { full: [], initials: [] };

  const parts = cleaned.split(" ");
  const full = parts.filter((p) => p.length > 1).sort();
  const initials = parts.filter((p) => p.length === 1).sort();

  return { full, initials };
}

/**
 * Calculate per-word similarity threshold based on word length
 * @param {string} tok_a
 * @param {string} tok_b
 * @returns {number}
 */
function wordGate(tok_a, tok_b) {
  const n = Math.max(tok_a.length, tok_b.length);
  const allowed = n <= 4 ? 1 : 2;
  return n > 0 ? (n - allowed) / n : 0.0;
}

/**
 * Normalize a name to canonical form (sorted, space-separated tokens)
 * @param {string} name
 * @returns {string}
 */
function normalizeName(name) {
  const { full } = splitTokens(name);
  return full.join(" ");
}

/**
 * Generate all permutations of indices
 * @param {number} n
 * @param {number} k
 * @returns {number[][]}
 */
function* generatePermutations(n, k) {
  if (k === 0) {
    yield [];
    return;
  }

  const indices = Array.from({ length: n }, (_, i) => i);

  function* generate(used, current) {
    if (current.length === k) {
      yield [...current];
      return;
    }

    for (let i = 0; i < indices.length; i++) {
      if (!used.has(i)) {
        used.add(i);
        current.push(i);
        yield* generate(used, current);
        current.pop();
        used.delete(i);
      }
    }
  }

  yield* generate(new Set(), []);
}

/**
 * Main name matching function
 * @param {string} name_a
 * @param {string} name_b
 * @param {number} threshold - Default 0.90
 * @returns {Object} - { matched: boolean, similarity: number }
 */
function namesMatch(name_a, name_b, threshold = 0.9) {
  const raw_a = rawWordCount(name_a);
  const raw_b = rawWordCount(name_b);
  const bothOriginallyMulti = raw_a >= 2 && raw_b >= 2;

  // Single-word names: too risky to drop unless single-owner context
  if (!bothOriginallyMulti) {
    if (threshold > 0.7) {
      return { matched: false, similarity: 0.0 };
    }

    // Single-owner: find the single token's best match
    const { full: full_a } = splitTokens(name_a);
    const { full: full_b } = splitTokens(name_b);

    const single = full_a[0] || "";
    const multi = full_b;

    if (!single || !multi.length) {
      return { matched: false, similarity: 0.0 };
    }

    let bestScore = 0.0;
    for (const token of multi) {
      const score = calculateSimilarity(single, token);
      if (score > bestScore) {
        bestScore = score;
      }
    }

    const longestMulti = multi.reduce(
      (max, token) => (token.length > max.length ? token : max),
      ""
    );
    const gate = wordGate(single, longestMulti);

    return {
      matched: bestScore >= gate,
      similarity: bestScore,
    };
  }

  // Split into full words and initials
  const { full: full_a, initials: init_a } = splitTokens(name_a);
  const { full: full_b, initials: init_b } = splitTokens(name_b);

  if (!full_a.length || !full_b.length) {
    return { matched: false, similarity: 0.0 };
  }

  // Shorter full-word list is reference; longer may have extras to drop
  let short_full, long_full, short_init;
  if (full_a.length <= full_b.length) {
    short_full = full_a;
    long_full = full_b;
    short_init = init_a;
  } else {
    short_full = full_b;
    long_full = full_a;
    short_init = init_b;
  }

  // Find optimal token matching using permutations
  const n_short = short_full.length;
  let bestAssignment = null;
  let bestTotal = -1.0;

  for (const perm of generatePermutations(long_full.length, n_short)) {
    let total = 0.0;
    for (let i = 0; i < n_short; i++) {
      total += calculateSimilarity(short_full[i], long_full[perm[i]]);
    }
    if (total > bestTotal) {
      bestTotal = total;
      bestAssignment = perm;
    }
  }

  if (!bestAssignment) {
    return { matched: false, similarity: 0.0 };
  }

  // Calculate word scores with best assignment
  const wordScores = [];
  for (let i = 0; i < n_short; i++) {
    const score = calculateSimilarity(
      short_full[i],
      long_full[bestAssignment[i]]
    );
    wordScores.push({
      score,
      shortToken: short_full[i],
      longToken: long_full[bestAssignment[i]],
    });
  }

  // Track assigned indices
  const assignedIndices = new Set(bestAssignment);
  const available = long_full.filter((_, idx) => !assignedIndices.has(idx));

  // Verify initials against dropped words
  const dropped = available;
  const sharedInitials = [
    ...new Set([...init_a].filter((x) => init_b.includes(x))),
  ];
  const initialsToVerify = short_init.filter(
    (i) => !sharedInitials.includes(i)
  );
  let unaccounted = [...dropped];

  for (const initial of initialsToVerify) {
    const matchedIndex = unaccounted.findIndex((word) => word[0] === initial);
    if (matchedIndex === -1) {
      return { matched: false, similarity: 0.0 };
    }
    unaccounted.splice(matchedIndex, 1);
  }

  // Check each word pair against its gate
  const scoresOnly = wordScores.map((ws) => ws.score);

  for (let i = 0; i < wordScores.length; i++) {
    const { score, shortToken: tok_s, longToken: tok_l } = wordScores[i];
    const baseGate = wordGate(tok_s, tok_l);

    const otherScores = scoresOnly.filter((_, j) => j !== i);
    const othersAllPerfect =
      otherScores.length > 0 && otherScores.every((s) => s >= 0.95);

    // When all other words match perfectly, check first character
    const firstCharDiffers = tok_s && tok_l && tok_s[0] !== tok_l[0];
    if (othersAllPerfect && firstCharDiffers) {
      return { matched: false, similarity: score };
    }

    const gate = baseGate;
    if (score < gate) {
      return { matched: false, similarity: score };
    }
  }

  if (wordScores.length === 0) {
    return { matched: false, similarity: 0.0 };
  }

  const similarity =
    wordScores.reduce((sum, ws) => sum + ws.score, 0) / wordScores.length;
  return { matched: true, similarity };
}

// Export functions
module.exports = {
  namesMatch,
  normalizeName,
  cleanName,
  splitTokens,
  wordGate,
  calculateSimilarity,
};
